import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'sv';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Frank AI Assistant
    'frank.welcome': 'Welcome to Frank',
    'frank.ai_assistant': 'AI Assistant',
    'frank.ask_placeholder': 'Ask Frank something...',
    'frank.typing': 'Frank is typing...',
    'frank.intro_title': 'Welcome to Frank',
    'frank.intro_description': "I'm your AI assistant. Ask me anything about your organization or any other question you have.",
    'frank.mobile_note': 'On mobile devices, ensure you have a stable internet connection for the best experience.',
    'frank.connection_error': 'Connection Error',
    'frank.retry_connection': 'Retry Connection',
    'frank.configure_api': 'Configure API settings',

    // Navigation
    'nav.features': 'Features',
    'nav.pricing': 'Pricing',
    'nav.contact': 'Contact us',
    'nav.login': 'Login',
    'nav.admin': 'Admin',
    'nav.documents': 'Documents',
    'nav.ask_frank': 'Ask Frank',
    'nav.profile': 'Profile',
    'nav.sign_out': 'Sign Out',

    // Home Page
    'home.hero.title': 'Meet Frank – your assistant who can answer faster than anyone else.',
    'home.hero.description': 'Communicate with your housing association, documents and members\' questions in a smart and efficient way.',
    'home.hero.demo_button': 'Book a demo',
    'home.hero.learn_more': 'Learn more',

    // Features Section
    'features.title': 'How it works',
    'features.upload.title': 'You upload your documents',
    'features.upload.description': 'Simple and secure document management',
    'features.access.title': 'Access to Frank via Mobile or Desktop',
    'features.access.description': 'Ask questions when it suits your members',
    'features.questions.title': 'Members ask questions',
    'features.questions.description': 'Fast and accurate information',
    'features.answer.title': 'Frank answers directly',
    'features.answer.description': 'Without disturbing the board',

    // Benefits Section
    'benefits.faster.title': 'Faster answers for members',
    'benefits.faster.description': 'Get answers immediately without waiting',
    'benefits.workload.title': 'Less workload for the board',
    'benefits.workload.description': 'Automate common questions',
    'benefits.secure.title': 'Secure, GDPR-compliant solution',
    'benefits.secure.description': 'Safe handling of organizational data',

    // CTA Section
    'cta.title': 'See how Frank answers questions',
    'cta.question': 'Can I sublet during the summer?',
    'cta.answer': 'According to our bylaws, you may sublet for a maximum of three months during vacation stays. See section 1c paragraph 12 for more information.',
    'cta.button': 'Get started today',

    // Footer
    'footer.tagline': 'Your Assistant for organizational management',
    'footer.product': 'Product',
    'footer.features': 'Features',
    'footer.pricing': 'Pricing',
    'footer.demo': 'Demo',
    'footer.support': 'Support',
    'footer.contact': 'Contact',
    'footer.help': 'Help',
    'footer.documentation': 'Documentation',
    'footer.company': 'Company',
    'footer.about': 'About us',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    'footer.copyright': '© 2024 Frank. All rights reserved.',

    // Dashboard
    'dashboard.welcome': 'Welcome to',
    'dashboard.greeting': 'Hello',
    'dashboard.subtitle': 'Stay updated with the latest news.',

    // News Feed
    'news.greeting': 'Hello {firstName}, Latest news from {organizationName}',
    'news.latest_news': 'Latest news',
    'news.loading': 'Loading news...',
    'news.no_news': 'No news to display',
    'news.no_news_subtitle': 'New articles will appear here when published',

    // Articles
    'articles.read_by': 'read by',
    'articles.of': 'of',
    'articles.members': 'members',

    // Documents Page
    'documents.title': 'Document Management',
    'documents.subtitle': 'Manage and organize your organizational documents',
    'documents.add_section': 'Add Section',
    'documents.cancel': 'Cancel',
    'documents.no_documents': 'No documents in this section',
    'documents.upload_documents': 'Upload Documents',
    'documents.documents_in': 'Documents in',
    'documents.back_to_sections': 'Back to Sections',
    'documents.select_section': 'Select a section to view documents',

    // Profile Page
    'profile.title': 'Profile Settings',
    'profile.full_name': 'Full Name',
    'profile.email': 'Email',
    'profile.phone': 'Phone Number',
    'profile.phone_placeholder': 'Enter phone number',
    'profile.current_password': 'Current Password',
    'profile.current_password_placeholder': 'Enter current password',
    'profile.new_password': 'New Password',
    'profile.new_password_placeholder': 'Enter new password',
    'profile.confirm_password': 'Confirm New Password',
    'profile.confirm_password_placeholder': 'Confirm new password',
    'profile.save_changes': 'Save Changes',
    'profile.saving': 'Saving...',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No'
  },
  sv: {
    // Frank AI Assistant
    'frank.welcome': 'Välkommen till Frank',
    'frank.ai_assistant': 'AI-assistent',
    'frank.ask_placeholder': 'Fråga Frank något...',
    'frank.typing': 'Frank skriver...',
    'frank.intro_title': 'Välkommen till Frank',
    'frank.intro_description': 'Jag är din AI-assistent. Fråga mig vad som helst om din organisation eller andra frågor du har.',
    'frank.mobile_note': 'På mobila enheter, se till att du har en stabil internetanslutning för bästa upplevelse.',
    'frank.connection_error': 'Anslutningsfel',
    'frank.retry_connection': 'Försök igen',
    'frank.configure_api': 'Konfigurera API-inställningar',

    // Navigation
    'nav.features': 'Funktioner',
    'nav.pricing': 'Priser',
    'nav.contact': 'Kontakta oss',
    'nav.login': 'Logga in',
    'nav.admin': 'Admin',
    'nav.documents': 'Dokument',
    'nav.ask_frank': 'Fråga Frank',
    'nav.profile': 'Profil',
    'nav.sign_out': 'Logga ut',

    // Home Page
    'home.hero.title': 'Möt Frank – er assistent som kan svara snabbare än någon annan.',
    'home.hero.description': 'Kommunicera med er bostadsrätt, dokument och medlemmarnas frågor på ett smart och effektivt sätt.',
    'home.hero.demo_button': 'Boka en demo',
    'home.hero.learn_more': 'Läs mer',

    // Features Section
    'features.title': 'Så här fungerar det',
    'features.upload.title': 'Ni laddar upp era dokument',
    'features.upload.description': 'Enkelt och säkert dokumenthantering',
    'features.access.title': 'Access till Frank via Mobil eller Desktop',
    'features.access.description': 'Ställ frågor när det passar era medlemmar',
    'features.questions.title': 'Medlemmar ställer frågor',
    'features.questions.description': 'Snabb och korrekt information',
    'features.answer.title': 'Frank svarar direkt',
    'features.answer.description': 'Utan att störa styrelsen',

    // Benefits Section
    'benefits.faster.title': 'Snabbare svar för medlemmar',
    'benefits.faster.description': 'Få svar direkt utan väntetid',
    'benefits.workload.title': 'Mindre arbetsbörda för styrelsen',
    'benefits.workload.description': 'Automatisera vanliga frågor',
    'benefits.secure.title': 'Säker, GDPR-anpassad lösning',
    'benefits.secure.description': 'Trygg hantering av organisationsdata',

    // CTA Section
    'cta.title': 'Se hur Frank svarar på frågor',
    'cta.question': 'Får jag hyra ut i andra hand under sommaren?',
    'cta.answer': 'Enligt våra stadgar får du hyra ut i andra hand under maximalt tre månader vid semestervistelse. Se 1c paragraf 12 för mer information.',
    'cta.button': 'Kom igång idag',

    // Footer
    'footer.tagline': 'Din Assistent för organisationshantering',
    'footer.product': 'Produkt',
    'footer.features': 'Funktioner',
    'footer.pricing': 'Priser',
    'footer.demo': 'Demo',
    'footer.support': 'Support',
    'footer.contact': 'Kontakt',
    'footer.help': 'Hjälp',
    'footer.documentation': 'Dokumentation',
    'footer.company': 'Företag',
    'footer.about': 'Om oss',
    'footer.privacy': 'Integritet',
    'footer.terms': 'Villkor',
    'footer.copyright': '© 2024 Frank. Alla rättigheter förbehållna.',

    // Dashboard
    'dashboard.welcome': 'Välkommen till',
    'dashboard.greeting': 'Hej',
    'dashboard.subtitle': 'Håll dig uppdaterad med de senaste nyheterna.',

    // News Feed
    'news.greeting': 'Hej {firstName}, Senaste nytt från {organizationName}',
    'news.latest_news': 'Senaste nytt',
    'news.loading': 'Laddar nyheter...',
    'news.no_news': 'Inga nyheter att visa',
    'news.no_news_subtitle': 'Nya artiklar kommer att visas här när de publiceras',

    // Articles
    'articles.read_by': 'läst av',
    'articles.of': 'av',
    'articles.members': 'medlemmar',

    // Documents Page
    'documents.title': 'Dokumenthantering',
    'documents.subtitle': 'Hantera och organisera era organisationsdokument',
    'documents.add_section': 'Lägg till sektion',
    'documents.cancel': 'Avbryt',
    'documents.no_documents': 'Inga dokument i denna sektion',
    'documents.upload_documents': 'Ladda upp dokument',
    'documents.documents_in': 'Dokument i',
    'documents.back_to_sections': 'Tillbaka till sektioner',
    'documents.select_section': 'Välj en sektion för att visa dokument',

    // Profile Page
    'profile.title': 'Profilinställningar',
    'profile.full_name': 'Fullständigt namn',
    'profile.email': 'E-post',
    'profile.phone': 'Telefonnummer',
    'profile.phone_placeholder': 'Ange telefonnummer',
    'profile.current_password': 'Nuvarande lösenord',
    'profile.current_password_placeholder': 'Ange nuvarande lösenord',
    'profile.new_password': 'Nytt lösenord',
    'profile.new_password_placeholder': 'Ange nytt lösenord',
    'profile.confirm_password': 'Bekräfta nytt lösenord',
    'profile.confirm_password_placeholder': 'Bekräfta nytt lösenord',
    'profile.save_changes': 'Spara ändringar',
    'profile.saving': 'Sparar...',

    // Common
    'common.loading': 'Laddar...',
    'common.error': 'Fel',
    'common.save': 'Spara',
    'common.cancel': 'Avbryt',
    'common.delete': 'Ta bort',
    'common.edit': 'Redigera',
    'common.create': 'Skapa',
    'common.back': 'Tillbaka',
    'common.next': 'Nästa',
    'common.previous': 'Föregående',
    'common.close': 'Stäng',
    'common.confirm': 'Bekräfta',
    'common.yes': 'Ja',
    'common.no': 'Nej'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('frank-language');
    return (saved as Language) || 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('frank-language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  useEffect(() => {
    localStorage.setItem('frank-language', language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
